import React, { useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { useWebsiteNotifications } from '../../../hooks/useWebsiteNotifications'
import { formatSecondsAgo } from '../../../utils/dateUtils'

// Shared notification bell + dropdown, mounted in both DesktopHeader and
// MobileHeader. Polling/read-state logic lives entirely in
// useWebsiteNotifications, so both mount points stay in sync with one
// underlying query.
const WebsiteNotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)
  const { notifications, unreadCount, isLoading, markRead, markAllRead } = useWebsiteNotifications()

  useEffect(() => {
    if (!isOpen) return
    const handleDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', handleDocClick)
    return () => document.removeEventListener('click', handleDocClick)
  }, [isOpen])

  const toggleOpen = (e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
    setIsOpen((prev) => !prev)
  }

  const handleItemClick = (notification) => {
    if (!notification.is_read) markRead(notification.id)
  }

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount)

  return (
    <span ref={containerRef} style={{ position: 'relative' }} role="button" aria-haspopup="dialog" aria-controls="websiteNotificationsPanel">
      <span onClick={toggleOpen} className="kq-icon-pill kq-icon-pill--square" style={{ position: 'relative' }}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}
          aria-hidden="true"
        >
          <path d="M12 22c1.104 0 2-.896 2-2h-4c0 1.104.896 2 2 2z" fill="#fff" />
          <path d="M18 16v-5c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 10-3 0v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              minWidth: 15,
              height: 15,
              padding: '0 3px',
              borderRadius: 8,
              background: '#F6255A',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '15px',
              textAlign: 'center',
              border: '1.5px solid #171821',
            }}
          >
            {badgeLabel}
          </span>
        )}
      </span>

      {isOpen && (
        <div
          id="websiteNotificationsPanel"
          role="dialog"
          aria-label="Notifications"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 32,
            right: 0,
            width: 320,
            background: '#1F2230',
            color: '#E7E9EF',
            border: '1px solid #2E3244',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            overflow: 'hidden',
            zIndex: 1050,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 12px',
              background: '#1B1E2A',
              borderBottom: '1px solid #2E3244',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 14 }}>Notifications</div>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead()}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#8FA6FF',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          <SimpleBar style={{ maxHeight: 320 }}>
            {isLoading && notifications.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: '#9AA0AE' }}>Loading…</div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: '#9AA0AE' }}>No notifications yet</div>
            ) : (
              notifications.map((notification) => {
                const rowStyle = {
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 12px',
                  borderBottom: '1px solid #2A2D3E',
                  cursor: notification.is_read && !notification.link ? 'default' : 'pointer',
                  background: notification.is_read ? 'transparent' : 'rgba(246,37,90,0.06)',
                  color: 'inherit',
                  textDecoration: 'none',
                }
                const rowContent = (
                  <>
                    <span
                      style={{
                        marginTop: 6,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: notification.is_read ? 'transparent' : '#F6255A',
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{notification.title}</div>
                      {notification.body && (
                        <div style={{ fontSize: 12, color: '#9AA0AE', marginTop: 2 }}>{notification.body}</div>
                      )}
                      <div style={{ fontSize: 11, color: '#6B7080', marginTop: 4 }}>
                        {formatSecondsAgo(notification.created_at_seconds_ago)}
                      </div>
                    </div>
                  </>
                )

                return notification.link ? (
                  <a
                    key={notification.id}
                    href={notification.link}
                    onClick={() => handleItemClick(notification)}
                    style={rowStyle}
                  >
                    {rowContent}
                  </a>
                ) : (
                  <div key={notification.id} onClick={() => handleItemClick(notification)} style={rowStyle}>
                    {rowContent}
                  </div>
                )
              })
            )}
          </SimpleBar>
        </div>
      )}
    </span>
  )
}

export default WebsiteNotificationBell
